import { ok } from '@src/services/common/Result';
import { deepCloneSafe } from '@src/utility/deepCloneSafe';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable } from 'mobx';

import type { Result } from '@src/services/common/Result';
import type { ToastRequest } from '@src/stores/toast';
import type { VideoStateSnapshot } from '@src/stores/VideoCreatorStore';
import type { VideoVersion } from '@src/types.ts/story';

const logger = LogManager.getLogger('store:ClipVersionPreviewStore');

/** Minimum gap between "edits are paused" toasts while previewing a version. */
const PREVIEW_EDIT_TOAST_INTERVAL_MS = 5000;

/** What the store needs from the clip editor it previews versions in. */
export interface ClipVersionPreviewHost {
  /** Fetch the resources a version references, so it can be shown. */
  loadVersion(version: VideoVersion): Promise<Result<VideoVersion>>;
  /** Land in-flight edits, so the live clip set aside is also what is persisted. */
  landPendingSave(): Promise<void>;
  /** The live clip as the editor holds it. */
  buildSnapshot(): VideoStateSnapshot;
  /** Show a snapshot in the editor. */
  applySnapshot(snapshot: VideoStateSnapshot): void;
  /** Keep what the editor shows and save it, undoable back to `current`. */
  restore(current: VideoStateSnapshot): void;
  showToast(toast: ToastRequest): number;
  dismissToast(id: number): void;
}

/**
 * A saved version's timeline laid over the live clip, so what is not timeline
 * state (parent/associated videos, live reel names via `applySnapshot`) stays
 * as it is.
 */
export function overlayVersion(
  current: VideoStateSnapshot,
  version: VideoVersion,
): VideoStateSnapshot {
  // Cloned: `applySnapshot` adopts and mutates what it is handed, and the
  // spread shares `current`'s nested objects, which must survive for leaving.
  return deepCloneSafe({
    ...current,
    videoSource: version.videoSource,
    transcriptionSnapshot:
      version.transcriptionSnapshot ?? current.transcriptionSnapshot,
    karaokeData: version.karaokeData ?? current.karaokeData,
    karaokeDataByLanguage:
      version.karaokeDataByLanguage ?? current.karaokeDataByLanguage,
    scenes: version.scenes ?? [],
    extraElementData: version.extraElementData ?? current.extraElementData,
    clipJson: version.clipJson ?? current.clipJson,
  });
}

/**
 * Clip History preview: shows a saved version of the clip read-only, until
 * Restore (`commit`) keeps it or Back to Current (`exit`) puts the live clip
 * back. Owns the pick too, so whatever ends the preview also drops a version
 * still loading.
 */
export class ClipVersionPreviewStore {
  /**
   * The saved version shown read-only in the editor, or null. While set, edits
   * are ignored and nothing saves.
   */
  previewingVersion: VideoVersion | null = null;
  /** The pick whose resources are still loading. */
  pendingVersion: VideoVersion | null = null;
  /** Why the last pick did not show. */
  error: string | null = null;

  /** The live clip as it was when the preview began — what leaving returns to. */
  private _currentBeforePreview: VideoStateSnapshot | null = null;
  /** Bumped by every pick and every way out; a load shows only while latest. */
  private _pickId = 0;
  /**
   * Versions already loaded, so returning to one is instant. Saved versions
   * never change, and version ids are unique across records.
   */
  private _loaded = new Map<string, VideoVersion>();
  /** When the "edits are paused" toast last showed — a drag fires many commands. */
  private _pausedToastAt = 0;
  /** That toast, while up — it is about the preview, so it ends with it. */
  private _pausedToastId: number | null = null;
  private readonly _host: ClipVersionPreviewHost;

  constructor(host: ClipVersionPreviewHost) {
    this._host = host;
    makeAutoObservable<
      ClipVersionPreviewStore,
      | '_currentBeforePreview'
      | '_pickId'
      | '_loaded'
      | '_pausedToastAt'
      | '_pausedToastId'
      | '_host'
    >(
      this,
      {
        previewingVersion: observable.ref,
        pendingVersion: observable.ref,
        _currentBeforePreview: false,
        _pickId: false,
        _loaded: false,
        _pausedToastAt: false,
        _pausedToastId: false,
        _host: false,
      },
      { autoBind: true },
    );
  }

  get isPreviewing(): boolean {
    return this.previewingVersion !== null;
  }

  /**
   * Preview `version`. The live clip is set aside on the first one; another
   * swaps in place. Only the latest pick shows, whichever load finishes first,
   * and picking the version already showing just drops a pick still loading.
   */
  async select(version: VideoVersion): Promise<void> {
    const pickId = ++this._pickId;
    this.error = null;
    if (version.versionId === this.previewingVersion?.versionId) {
      this.pendingVersion = null;
      return;
    }
    this.pendingVersion = version;
    // The first preview waits for pending edits to land; start that now so it
    // overlaps the load. A preview already showing has nothing pending (edits
    // are ignored).
    const [loaded] = await Promise.all([
      this.load(version),
      this.previewingVersion ? undefined : this._host.landPendingSave(),
    ]);
    if (pickId !== this._pickId) return;
    this.show(loaded);
  }

  /** Back to Current: put the live clip back. Nothing was saved, so nothing saves. */
  exit(): void {
    const current = this.end();
    if (current) this._host.applySnapshot(current);
  }

  /**
   * Restore: keep the version showing and save it as the new head. Undoable —
   * back to the live clip the preview started from.
   */
  commit(): void {
    const current = this.end();
    if (current) this._host.restore(current);
  }

  /**
   * Say why an edit did nothing while a version is previewed, with the way out.
   * At most once per burst: one drag is many commands.
   */
  explainPausedEdit(): void {
    const now = Date.now();
    if (now - this._pausedToastAt < PREVIEW_EDIT_TOAST_INTERVAL_MS) return;
    this._pausedToastAt = now;
    this._pausedToastId = this._host.showToast({
      state: 'info',
      message:
        "You're viewing an older version. Restore it or go back to the current version to edit.",
      // Trying to edit is the strongest sign Restore is what's wanted.
      action: {
        label: 'Restore This Version',
        onClick: () => this.commit(),
      },
      // A secondary action keeps the toast up by design; leaving the preview
      // closes it (`end`).
      secondaryAction: {
        label: 'Back to Current',
        onClick: () => this.exit(),
      },
    });
  }

  private async load(version: VideoVersion): Promise<Result<VideoVersion>> {
    const cached = this._loaded.get(version.versionId);
    if (cached) return ok(cached);
    const loaded = await this._host.loadVersion(version);
    if (loaded.ok) this._loaded.set(version.versionId, loaded.value);
    return loaded;
  }

  private show(loaded: Result<VideoVersion>): void {
    this.pendingVersion = null;
    if (!loaded.ok) {
      this.error = 'Could not open this version. Please try again.';
      return;
    }
    const current = this._currentBeforePreview ?? this._host.buildSnapshot();
    this._currentBeforePreview = current;
    this.previewingVersion = loaded.value;
    try {
      this._host.applySnapshot(overlayVersion(current, loaded.value));
    } catch (e) {
      logger.error('Failed to preview clip version', {
        versionId: loaded.value.versionId,
        ...extractErrorInfo(e),
      });
      this.error = 'Could not open this version. Please try again.';
    }
  }

  /**
   * Leave the preview: drop any pick still loading, and close the toast
   * explaining the preview. Returns the live clip set aside, if one was.
   */
  private end(): VideoStateSnapshot | null {
    this._pickId++;
    this.pendingVersion = null;
    const current = this.previewingVersion ? this._currentBeforePreview : null;
    this.previewingVersion = null;
    this._currentBeforePreview = null;
    if (this._pausedToastId !== null) {
      this._host.dismissToast(this._pausedToastId);
      this._pausedToastId = null;
    }
    return current;
  }
}
